// @flow
import React, { type Node } from 'react';
import { hot } from 'react-hot-loader';
import Helmet from 'react-helmet-async';
import { connect } from 'react-redux';
import { compose, pure } from 'recompose';
import { Alert, Grid, Row, Col } from 'angieslist-react-components';
import { legacyAnalytics, urlCatalog } from 'angieslist-app-platform';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import { get, isUndefined, orderBy, find } from 'lodash';
import reviewAnalytics from './reviewAnalytics';
import type { BrowserReducerState } from '../types';
import type { ReviewsReducerState } from '../common/reviewsReducer';
import reviewsActions from '../common/reviewsActions';

import CategoryList from './components/reviews/CategoryList';
import RatingFilter from './components/reviews/RatingFilter';
import HowDisplayedModal from './components/reviews/HowDisplayedModal';
import MobileCategoryList from './components/reviews/MobileCategoryList';
import MobileRatingFilter from './components/reviews/MobileRatingFilter';
import MobileSortingOptions from './components/reviews/MobileSortingOptions';
import RequestReviewsCta from './components/reviews/RequestReviewsCta';
import ReviewCards from './components/reviews/ReviewCards';
import ReviewRatingDetails from './components/reviews/ReviewRatingDetails';
import SortingOptions from './components/reviews/SortingOptions';

export type ReviewsViewProps = {
  // redux state
  browser: BrowserReducerState,
  reviews: ReviewsReducerState,
  // redux actions
  getReviews: typeof reviewsActions.getReviews,
  getReviewCategories: typeof reviewsActions.getReviewCategories,
  // router
  location: {
    query?: {
      categoryId: string
    }
  },
  params: {
    serviceProviderId: string
  },
};

const defaultUIOptions = Object.freeze({
  categoryId: 0,
  starRatingFilters: [],
  sortDirection: 'desc',
  sortType: 'recency',
  offset: 0,
});

export class ReviewsViewComponent extends React.Component<ReviewsViewProps> {
  componentDidMount() {
    const {
      getReviewCategories,
      params: { serviceProviderId },
    } = this.props;
    getReviewCategories(serviceProviderId);
  }
  componentDidUpdate() {
    // Handle calculating categoryId and getting reviews
    const {
      reviews: {
        reviews: {
          request: reviewsRequest,
          isLoading: reviewsLoading,
        },
      },
      getReviews,
      location: {
        query,
      },
      params: {
        serviceProviderId,
      },
    } = this.props;

    const {
      categoryId: requestedCategoryId,
      starRatingFilters,
      sortDirection,
      sortType,
    } = reviewsRequest || defaultUIOptions;
    const locationCategoryId = Number(get(query, 'categoryId', 0));
    // category changed if 1) query vs request are different and
    // 2) query and request aren't both falsey
    const categoryChanged = (locationCategoryId !== requestedCategoryId &&
      (locationCategoryId || requestedCategoryId));
    if ((!reviewsRequest || categoryChanged) && !reviewsLoading) {
      getReviews(
        serviceProviderId,
        locationCategoryId,
        starRatingFilters,
        sortDirection,
        sortType,
        0,
        true,
      );
    }
  }
  props: ReviewsViewProps;
  render(): Node {
    const {
      browser,
      reviews: {
        reviews: {
          isLoading: reviewsLoading,
          response: review,
          request: reviewsRequest,
          error: reviewsError,
        },
        reviewCategories: {
          isLoading: reviewCategoriesLoading,
          response: reviewCategories,
          error: reviewCategoriesError,
        },
      },
      getReviews,
      params: {
        serviceProviderId,
      },
    } = this.props;

    const requestedReviewPURL = urlCatalog.visitor.writeReview(serviceProviderId, {
      cid: 'PRL.E014.P026.20180302',
    });
    const {
      categories, metricAggregation, displayStarRating, displayReviewCount,
    } = reviewCategories || {
      categories: [],
      metricAggregation: {},
      displayStarRating: undefined,
      displayReviewCount: 0,
    };
    const {
      categoryId,
      starRatingFilters,
      sortDirection,
      sortType,
      offset,
    } = reviewsRequest || defaultUIOptions;
    const effectiveCategoryId = categoryId || 0;
    const { reviews = [], totalReviewCount: reviewCount = 0 } = review || {};
    const allReviewsCategory = {
      id: 0,
      name: 'All',
      reviewMetrics: {
        count: get(metricAggregation, 'count', 0),
        starRatingCounts: get(metricAggregation, 'starRatingCounts'),
        ratings: get(metricAggregation, 'ratings'),
      },
      displayStarRating,
      displayReviewCount,
      optIns: [],
    };
    const selectedCategory = find(categories, c => c.id === effectiveCategoryId)
      || allReviewsCategory;
    const error = reviewCategoriesError || reviewsError;
    const finishedLoading = !reviewCategoriesLoading && !reviewsLoading;
    const categoriesWithAllOption = [allReviewsCategory, ...orderBy(categories, ['displayReviewCount', 'name'], ['desc', 'asc'])];
    const ratingsMetrics = get(metricAggregation, 'starRatingCounts', {});
    const ratingDetails = get(metricAggregation, 'ratings', []);


    return (
      <Grid>
        <Helmet>
          <title>Reviews</title>
        </Helmet>
        <Row>
          <Col xs={12}>
            <h1>
              Reviews
            </h1>
            <If condition={error}>
              <Alert bsStyle="danger">There has been an error.</Alert>
            </If>
            <RequestReviewsCta
              serviceProviderId={serviceProviderId}
              browser={browser}
              requestedReviewPURL={requestedReviewPURL}
            />
          </Col>
        </Row>
        <Loader
          loading={isUndefined(reviewCategories) || reviewCategoriesLoading || reviewsLoading}
          element={
            <Row className="text-center" >
              <i className="icon icon-load icon-spin icon-3x text-primary" />
            </Row>}
        >
          <Row
            className="review-tab-container"
          >

            <Col xs={12} sm={3}>
              <div className="hidden-sm hidden-md hidden-lg">
                <MobileCategoryList
                  categoryId={categoryId}
                  serviceProviderId={serviceProviderId}
                  categories={categoriesWithAllOption}
                  onSelect={(analyticsProps) => {}}
                />
              </div>
              <div className="hidden-xs">
                <CategoryList
                  categoryId={categoryId}
                  serviceProviderId={serviceProviderId}
                  categories={categoriesWithAllOption}
                />
              </div>
              <div className="hidden-xs">
                <RatingFilter
                  categoryId={effectiveCategoryId}
                  serviceProviderId={serviceProviderId}
                  starRatingFilters={starRatingFilters}
                  categories={categoriesWithAllOption}
                  onSelect={(filters) => {
                      getReviews(
                        serviceProviderId,
                        effectiveCategoryId,
                        filters,
                        sortDirection,
                        sortType,
                      );
                    }}
                />
              </div>
              <HowDisplayedModal categoryId={categoryId} />
            </Col>
            <Col xs={12} sm={9}>
              <ReviewRatingDetails
                category={selectedCategory}
                ratingsMetrics={ratingsMetrics}
                ratingDetails={ratingDetails}
                displayStarRating={displayStarRating}
                displayReviewCount={displayReviewCount}
              />
              <div className="hidden-xs">
                <SortingOptions
                  sortType={sortType}
                  sortDirection={sortDirection}
                  categoryId={effectiveCategoryId}
                  serviceProviderId={serviceProviderId}
                  onSelect={(options: string[]) =>
                      getReviews(
                        serviceProviderId,
                        effectiveCategoryId,
                        starRatingFilters,
                        options[1],
                        options[0],
                      )
                    }
                />
              </div>
              <Row className="hidden-sm hidden-md hidden-lg">
                <Col xs={6}>
                  <MobileRatingFilter
                    categoryId={effectiveCategoryId}
                    serviceProviderId={serviceProviderId}
                    starRatingFilters={starRatingFilters}
                    categories={categoriesWithAllOption}
                    onSelect={(filters, analyticsProps) =>
                        getReviews(
                          serviceProviderId,
                          effectiveCategoryId,
                          filters,
                          sortDirection,
                          sortType,
                        )
                      }
                  />
                </Col>
                <Col xs={6}>
                  <MobileSortingOptions
                    sortType={sortType}
                    sortDirection={sortDirection}
                    categoryId={categoryId}
                    serviceProviderId={serviceProviderId}
                    onSelect={(options: { sortDirection: string, sortType: string }) =>
                        getReviews(
                          serviceProviderId,
                          effectiveCategoryId,
                          starRatingFilters,
                          options.sortDirection,
                          options.sortType,
                        )
                      }
                  />
                </Col>
              </Row>
              <ReviewCards
                reviews={reviews}
                reviewCount={reviewCount}
                offset={offset}
                categoryId={categoryId || 0}
                serviceProviderId={serviceProviderId}
                getReviews={getReviews}
                browser={browser}
                sortType={sortType}
                sortDirection={sortDirection}
                starRatingFilters={starRatingFilters}
                finishedLoading={finishedLoading}
              />
            </Col>
          </Row>
        </Loader>
      </Grid>
    );
  }
}

export default (hot(module)(compose(
  legacyAnalytics({
    ruleset: reviewAnalytics,
  }),
  connect(
    state => ({
      browser: state.browser,
      reviews: state.reviews,
    }),
    reviewsActions,
  ),
  pure,
)(ReviewsViewComponent)): any);



// WEBPACK FOOTER //
// ./src/modules/store/browser/ReviewsView.js